import * as Sentry from '@sentry/react';
import { type ServiceUser } from '@sr/types/subscription';
import { env } from 'customer-portal-helpers/env';
import { type ConfirmationPartner } from '../Checkout/Confirmation/types';
import { getApiUrl } from '../../utils/environment';

const BASE_PATH_USER_ADMIN = env(
  'PORTAL_RECYCLING_USER_ADMIN_BASE_PATH',
  '/stenarecycling/usermanagement',
);
const userAdminApiUrl = `${getApiUrl()}${BASE_PATH_USER_ADMIN}`;

export const fetchUserById = async (
  userId: string,
  accessToken: string,
  transactionId: string,
): Promise<ServiceUser> => {
  const url = `${userAdminApiUrl}/user/${encodeURIComponent(userId)}`;
  const headers = new Headers();

  headers.set('Authorization', `Bearer ${accessToken}`);
  headers.set('X-Transaction-Id', transactionId);

  const res = await fetch(url, { headers });

  if (!res.ok) {
    const responseBody = await res
      .clone()
      .text()
      .catch(() => 'Unable to read response body');

    Sentry.captureException(new Error(`Failed to fetch user: ${res.status} ${responseBody}`), {
      extra: {
        url,
        status: res.status,
        statusText: res.statusText,
        method: 'GET',
        responseBody,
        headers: Object.fromEntries(headers.entries()),
      },
      tags: {
        fetch_error: true,
      },
    });

    throw new Error(`Failed to fetch user: ${res.status} ${responseBody}`);
  }

  return (await res.json()) as ServiceUser;
};

export const fetchPartnerInfo = async (
  partnerIds: string[],
  accessToken: string,
  transactionId: string,
): Promise<ConfirmationPartner[]> => {
  const url = `${getApiUrl()}/v2/partners`;
  const headers = new Headers();

  headers.set('Authorization', `Bearer ${accessToken}`);
  headers.set('Content-Type', 'application/json');
  headers.set('X-Transaction-Id', transactionId);

  const fetchOptions = {
    method: 'POST',
    headers,
    body: JSON.stringify(partnerIds),
  };
  const res = await fetch(url, fetchOptions);

  if (!res.ok) {
    const responseBody = await res
      .clone()
      .text()
      .catch(() => 'Unable to read response body');

    Sentry.captureException(
      new Error(`Failed to fetch partner info: ${res.status} ${responseBody}`),
      {
        extra: {
          url,
          status: res.status,
          statusText: res.statusText,
          method: fetchOptions.method,
          body: fetchOptions.body,
          responseBody,
          headers: Object.fromEntries(headers.entries()),
        },
        tags: {
          fetch_error: true,
        },
      },
    );

    throw new Error(`Failed to fetch partner info: ${res.status} ${responseBody}`);
  }

  const data = (await res.json()) as {
    id: string;
    name: string;
    street?: string;
    city?: string;
    postalCode?: string;
  }[];

  return data.map((partner) => ({
    id: partner.id,
    name: partner.name,
    address: {
      street: partner.street?.replace('N/A', '') || undefined,
      city: partner.city,
      zipcode: partner.postalCode,
    },
  }));
};
